import {
  formatCatalogDimensionValue,
  sceneDimensionsToCatalog,
} from '@utils/dimensions/catalogDimensions'
import {
  pickItemFabricTexture,
  resolveFabricPanelLabels,
  toGroupTitleCase,
} from '@utils/budget/fabricCharacteristics'
import { localizeCatalogLabel } from '@utils/i18n/catalogLabels'
import { getDimensionAbbrevLabels, ui } from '@utils/i18n/uiCopy'
import { resolveSelectedLinkedUnit } from '@utils/sceneObjects/items/duplicateLinkedUnit'
import { isKubeCushion } from '@utils/sceneObjects/items/rinconeroUtils'
import { AnimatePresence, motion } from 'framer-motion'
import type { AnyID, LinkedItem } from '@types'
import { useMemo } from 'react'

type SelectedUnitInfoProps = {
  items: Map<AnyID, any>
  selectedItems: Set<string> | Set<AnyID>
  currentObjectID?: AnyID | string | ''
}

const isArmCatalog = (catalogID: unknown) => /^BR\d+(-\d+)?$/i.test(String(catalogID || ''))

const isCushionCatalog = (catalogID: unknown, referencia?: unknown) => {
  const catalog = String(catalogID || referencia || '').trim()
  return isKubeCushion(catalog) || /^K\d+C\b/i.test(catalog)
}

const titleClassName =
  'font-century text-[15px] font-semibold leading-snug tracking-[0.01em] text-[#2c2c2c] min-[2400px]:text-[19px]'

/** Código corto de unidad: "K1RB 070 040 051" → "K1RB". */
const shortCatalogCode = (item: { referencia?: unknown; catalogID?: unknown }) => {
  const raw = String(item?.referencia || item?.catalogID || '').trim()
  if (!raw) return '—'
  if (isArmCatalog(raw)) return raw.replace(/^BR(\d+)/i, 'B$1').toUpperCase()
  if (/^K\d+[A-Z]*/i.test(raw)) {
    return raw.replace(/(?:\s+\d+)+$/i, '').trim() || raw
  }
  return raw.replace(/-\d+$/i, '')
}

/** Referencia completa (para limpiar la descripción). */
const fullCatalogReference = (item: { referencia?: unknown; catalogID?: unknown }) => {
  const raw = String(item?.referencia || item?.catalogID || '').trim()
  if (!raw) return '—'
  if (isArmCatalog(raw)) return raw.replace(/^BR(\d+)/i, 'B$1').toUpperCase()
  return raw.replace(/-\d+$/i, '')
}

/** Quita el código de catálogo de la descripción para dejarlo en la 2ª línea. */
const cleanDescription = (description: string, catalogReference: string) => {
  let text = description.trim()
  if (catalogReference && catalogReference !== '—') {
    const escaped = catalogReference.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    text = text.replace(new RegExp(escaped, 'ig'), '').trim()
  }
  text = text.replace(/\s*K\d+[A-Z]*(?:\s+\d+)+\s*$/i, '').trim()
  text = text.replace(/[|/·,\-\s]+$/g, '').trim()
  return text
}

const resolveSelectedSceneItem = (
  items: Map<AnyID, any>,
  selectedItems: Iterable<string | AnyID>,
  currentObjectID?: AnyID | string | '',
): LinkedItem | null => {
  const unit = resolveSelectedLinkedUnit(items, selectedItems, currentObjectID)
  if (unit) return unit

  const ids = [
    ...(currentObjectID ? [String(currentObjectID)] : []),
    ...Array.from(selectedItems, String),
  ]
  for (const id of ids) {
    const item = items.get(id as AnyID)
    if (item?.type === 'linkedItem' && item.universal === false) return item as LinkedItem
  }
  return null
}

function DimMetric({ label, value }: { label: string; value: string }) {
  const numeric = value.replace(/cm$/i, '').trim()
  return (
    <span className='inline-flex items-baseline gap-1'>
      <span className='text-[9px] font-medium uppercase tracking-[0.14em] text-[#9a9a9a] min-[2400px]:text-[11px]'>
        {label}
      </span>
      <span className='font-century text-[12px] tabular-nums tracking-wide text-[#3a3a3a] min-[2400px]:text-[15px]'>
        {numeric}
        <span className='ml-0.5 text-[10px] font-normal text-[#8a8a8a] min-[2400px]:text-[12px]'>
          cm
        </span>
      </span>
    </span>
  )
}

/**
 * Chip discreto con la referencia y cotas de la unidad seleccionada.
 * Pensado para convivir con el toolbar sin competir por atención.
 */
export function SelectedUnitInfo({ items, selectedItems, currentObjectID }: SelectedUnitInfoProps) {
  const dimensionLabels = getDimensionAbbrevLabels()
  const selected = useMemo(
    () => resolveSelectedSceneItem(items, selectedItems, currentObjectID),
    [items, selectedItems, currentObjectID],
  )

  const info = useMemo(() => {
    if (!selected) return null

    const catalogFull = fullCatalogReference(selected)
    const catalogReference = shortCatalogCode(selected)
    const isArm = isArmCatalog(selected.catalogID)
    const isCushion = isCushionCatalog(selected.catalogID, selected.referencia)
    const description = String((selected as { description?: unknown }).description || '').trim()
    const cleanedHeadline = cleanDescription(description, catalogFull)
    const headline =
      localizeCatalogLabel(toGroupTitleCase(cleanedHeadline) || cleanedHeadline) || cleanedHeadline

    const dims = sceneDimensionsToCatalog({
      width: selected.width,
      thickness: selected.thickness,
      height: selected.height,
    })
    const largo = formatCatalogDimensionValue(dims.largo)
    const fondo = formatCatalogDimensionValue(dims.fondo)
    const alto = formatCatalogDimensionValue(dims.alto)

    const seatHeight = !isArm
      ? Number((selected as { seatHeightCm?: unknown }).seatHeightCm) || null
      : null

    const fabricTexture = pickItemFabricTexture(selected)
    const fabricLabels = resolveFabricPanelLabels(fabricTexture)
    const fabricUrl = String(fabricTexture?.url || '').trim()

    return {
      id: String(selected.id),
      headline,
      catalogReference,
      largo,
      fondo,
      alto,
      isArm,
      isCushion,
      seatHeight,
      fabricGroup: fabricLabels.group,
      fabricSubgroup: fabricLabels.subgroup,
      fabricTextureLabel: fabricLabels.textureLabel,
      fabricUrl: fabricUrl ? encodeURI(fabricUrl) : '',
    }
  }, [selected])

  return (
    <AnimatePresence mode='wait'>
      {info ? (
        <motion.div
          key={info.id}
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          className='pointer-events-none max-w-[min(420px,46vw)] select-none'
        >
          <div className='rounded-2xl border border-colorAtemporal bg-white px-3.5 py-2.5 shadow-lg min-[480px]:px-4 min-[480px]:py-3 min-[2400px]:rounded-3xl min-[2400px]:px-6 min-[2400px]:py-4'>
            <div className='mb-1.5 flex items-center gap-2 min-[2400px]:mb-2'>
              <span className='h-px w-3 bg-colorAtemporal/70 min-[2400px]:w-4' />
              <p className='text-[9px] font-medium uppercase tracking-[0.22em] text-colorAtemporal min-[2400px]:text-[11px]'>
                {ui('selection')}
              </p>
            </div>

            <div className='flex min-w-0 items-start gap-3'>
              {info.fabricUrl ? (
                <span
                  className='mt-0.5 h-9 w-9 shrink-0 rounded-lg border border-[#e6e0d2] bg-cover bg-center shadow-inner min-[2400px]:h-12 min-[2400px]:w-12 min-[2400px]:rounded-xl'
                  style={{ backgroundImage: `url('${info.fabricUrl}')` }}
                  aria-hidden
                />
              ) : null}

              <div className='min-w-0 flex-1'>
                {info.headline ? (
                  <p className={`${titleClassName} truncate`}>{info.headline}</p>
                ) : null}
                <p className={`${titleClassName} truncate ${info.headline ? 'mt-0.5' : ''}`}>
                  {ui('refPrefix')} {info.catalogReference}
                </p>

                {!info.isArm ? (
                  <div className='mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 min-[2400px]:mt-2.5 min-[2400px]:gap-x-3.5'>
                    <DimMetric
                      label={dimensionLabels.largo}
                      value={info.largo}
                    />
                    {!info.isCushion ? (
                      <>
                        <span
                          className='h-3 w-px bg-[#e5e0d4]'
                          aria-hidden
                        />
                        <DimMetric
                          label={dimensionLabels.fondo}
                          value={info.fondo}
                        />
                      </>
                    ) : null}
                    <span
                      className='h-3 w-px bg-[#e5e0d4]'
                      aria-hidden
                    />
                    <DimMetric
                      label={dimensionLabels.alto}
                      value={info.alto}
                    />
                  </div>
                ) : null}

                {info.seatHeight ? (
                  <p className='mt-1.5 text-[10.5px] leading-snug tracking-[0.01em] text-[#8d8d8d] min-[2400px]:mt-2 min-[2400px]:text-[13px]'>
                    {ui('seat')} {info.seatHeight} cm
                  </p>
                ) : null}

                {info.fabricGroup || info.fabricSubgroup || info.fabricTextureLabel ? (
                  <div
                    className='mt-1.5 text-[10.5px] leading-snug tracking-[0.01em] text-[#8d8d8d] min-[2400px]:mt-2 min-[2400px]:text-[13px]'
                    title={[info.fabricGroup, info.fabricSubgroup, info.fabricTextureLabel]
                      .filter(Boolean)
                      .join(' · ')}
                  >
                    {info.fabricGroup ? (
                      <p className='truncate font-semibold tracking-[0.01em] text-[#6f6f6f]'>
                        {info.fabricGroup}
                      </p>
                    ) : null}
                    {info.fabricSubgroup ? (
                      <p className='line-clamp-2'>{info.fabricSubgroup}</p>
                    ) : null}
                    {info.fabricTextureLabel ? <p>{info.fabricTextureLabel}</p> : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}
